import { RiBankCardLine, RiArchiveLine, RiTruckLine, RiCheckboxCircleLine } from "react-icons/ri";
import type { IconType } from "react-icons";
import type { BadgeTone, ReceiptCardSection } from "@dionatanbig2be/big2be-ui";
import { formatBRL } from "@/shared/helpers";
import type { EcmOrderStatus } from "../types/order.dto";
import type {
    OrderCard,
    OrderProgressStep,
    OrderRefundStatus,
    OrderStatus,
    PaymentMethod,
    ReceiptItem,
} from "../types/orders";

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
    pix: "Pix",
    wallet: "Carteira Virtual",
    boleto: "Boleto bancário",
    card: "Cartão de crédito",
};

export interface ProgressStepConfig {
    key: OrderProgressStep;
    /** `\n` renderiza em duas linhas (white-space: pre-line). */
    label: string;
    Icon: IconType;
}

export const PROGRESS_STEPS: ProgressStepConfig[] = [
    { key: "pagamento-aprovado", label: "Pagamento\nAprovado", Icon: RiBankCardLine },
    { key: "em-separacao", label: "Em\nSeparação", Icon: RiArchiveLine },
    { key: "em-rota", label: "Em rota de\nentrega", Icon: RiTruckLine },
    { key: "concluido", label: "Pedido\nconcluído", Icon: RiCheckboxCircleLine },
];

/** Índice (0-based) da etapa no stepper — -1 antes da aprovação do pagamento. */
export function progressIndex(step?: OrderProgressStep): number {
    return step ? PROGRESS_STEPS.findIndex((s) => s.key === step) : -1;
}

export { formatBRL };

/** Aparência de cada status agregado — strings literais para o JIT do Tailwind. */
export interface OrderStatusMeta {
    /** Rótulo do agregado (badge). O rótulo FINO da etapa vem de getStageLabel. */
    label: string;
    tone: BadgeTone;
    /** Disco do ícone: fundo suave + cor. */
    disc: string;
    /** Cor do texto do status. */
    text: string;
    /** Faixa de fundo suave (hero dos detalhes). */
    strip: string;
}

export const STATUS_META: Record<OrderStatus, OrderStatusMeta> = {
    "aguardando-pagamento": {
        label: "Aguardando pagamento",
        tone: "warning",
        disc: "bg-warning/[0.14] text-warning",
        text: "text-warning",
        strip: "bg-warning/[0.07]",
    },
    enviado: {
        label: "Em andamento",
        tone: "info",
        disc: "bg-info/[0.14] text-info",
        text: "text-info",
        strip: "bg-info/[0.07]",
    },
    concluido: {
        label: "Concluído",
        tone: "success",
        disc: "bg-success/[0.14] text-success",
        text: "text-success",
        strip: "bg-success/[0.07]",
    },
    cancelado: {
        label: "Cancelado",
        tone: "error",
        disc: "bg-error/[0.12] text-error",
        text: "text-error",
        strip: "bg-error/[0.06]",
    },
};

/**
 * Rótulo fino da etapa a partir do status cru do banco — conta mais história
 * que o agregado ("Em separação" em vez de "Em andamento").
 */
const STAGE_LABELS: Record<EcmOrderStatus, string> = {
    PENDING: "Aguardando pagamento",
    APPROVED: "Pagamento aprovado",
    SEPARATION: "Em separação",
    AWAITING_HANDOFF: "Pronto para envio",
    INVOICE_EMITED: "Em rota de entrega",
    COMPLETED: "Pedido concluído",
    ANTI_FRAUD_FAILED: "Cancelado",
    CANCELLED: "Cancelado",
    // O reembolso já aparece no badge próprio (getRefundLabel) — repetir aqui
    // deixaria "Reembolsado" duas vezes lado a lado no card.
    REFUNDED: "Cancelado",
};

export function getStatusLabel(status: OrderStatus): string {
    return STATUS_META[status].label;
}

export function getStageLabel(raw: EcmOrderStatus | undefined, fallback: OrderStatus): string {
    return (raw && STAGE_LABELS[raw]) || STATUS_META[fallback].label;
}

export function getRefundLabel(refundStatus: OrderRefundStatus): string | null {
    return refundStatus === "reembolsado" ? "Reembolsado" : null;
}

/** Monta os itens do comprovante a partir do pedido (com fallback no preview). */
export function buildReceiptItems(order: OrderCard): ReceiptItem[] {
    if (order.items?.length) {
        return order.items.map((it) => ({
            id: it.id,
            name: it.name,
            qty: it.qty,
            variantLabel: it.variantLabel ?? "",
            unitPriceLabel: formatBRL(it.unitPrice),
            imageUrl: it.imageUrl,
            imageAlt: it.imageAlt,
        }));
    }
    return order.itemsPreview.map((it) => ({
        id: it.id,
        name: it.label,
        qty: 1,
        variantLabel: it.variantLabel ?? "",
        unitPriceLabel: "—",
        imageUrl: it.imageUrl,
        imageAlt: it.imageAlt,
    }));
}

/**
 * Seções do ReceiptCard usado no comprovante exportado como imagem (PNG).
 * Fica aqui (e não no componente) porque é derivação pura de dados do pedido.
 */
export function buildReceiptSections(order: OrderCard): ReceiptCardSection[] {
    const metaRows: NonNullable<ReceiptCardSection["rows"]> = [];

    if (order.paymentMethod) {
        const details =
            order.paymentMethod === "card" && order.paymentDetails?.cardLastDigits
                ? ` •••• ${order.paymentDetails.cardLastDigits}`
                : order.paymentMethod === "wallet" && order.paymentDetails?.walletName
                  ? ` · ${order.paymentDetails.walletName}`
                  : "";
        metaRows.push({
            label: "Pagamento",
            value: `${PAYMENT_METHOD_LABELS[order.paymentMethod]}${details}`,
        });
        if (order.installment) {
            metaRows.push({
                label: `${order.installment.count}x de ${formatBRL(order.installment.amountPerInstallment)}`,
                value:
                    order.installment.interestRate > 0
                        ? `${order.installment.interestRate}% juros · Total: ${formatBRL(order.installment.totalWithInterest)}`
                        : "sem juros",
            });
        }
    }

    if (order.deliveryAddress) {
        const addr = order.deliveryAddress;
        if (order.shippingOption) {
            metaRows.push({
                label: "Entrega",
                value: `${order.shippingOption.label} · ${order.shippingOption.estimatedTime}`,
            });
        }
        metaRows.push({
            label: "Endereço",
            value: `${addr.label} — ${addr.street}, ${addr.number}${addr.complement ? ` (${addr.complement})` : ""}`,
        });
        if (addr.recipientName) {
            metaRows.push({
                label: "Destinatário",
                value: `${addr.recipientName}${addr.recipientPhone ? ` · ${addr.recipientPhone}` : ""}`,
            });
        }
        const region = [addr.neighborhood, addr.city, addr.state].filter(Boolean).join(", ");
        metaRows.push({ label: "CEP", value: [addr.zipCode, region].filter(Boolean).join(" · ") });
    }

    const result: ReceiptCardSection[] = [];
    if (metaRows.length > 0) result.push({ rows: metaRows });

    result.push({
        border: "top",
        itemsHeader: { start: "Produto", end: "Preço un." },
        items: buildReceiptItems(order).map((item) => ({
            id: item.id,
            name: item.name,
            quantity: `${item.qty}x${item.variantLabel ? ` · ${item.variantLabel}` : ""}`,
            price: item.unitPriceLabel,
        })),
    });

    if (order.breakdown) {
        result.push({
            border: "top",
            rows: [
                { label: "Subtotal", value: formatBRL(order.breakdown.subtotal) },
                ...(order.breakdown.discount > 0
                    ? [{ label: "Descontos", value: `− ${formatBRL(order.breakdown.discount)}` }]
                    : []),
            ],
        });
    }

    result.push({
        border: "top",
        rows: [{ label: "Total", value: order.total !== undefined ? formatBRL(order.total) : "—" }],
    });

    return result;
}
